import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import {
  getPrinterAgentStatuses,
  getPrintAgentLogs,
  queueTestPrint,
} from "Api/apiService";

const LOG_LEVELS = ["all", "info", "warn", "error"];
const AUTO_REFRESH_MS = 15000;

const formatDate = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) {
    return "-";
  }
  return parsed.toLocaleString("en-US");
};

const statusColor = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "online") return "green";
  if (normalized === "degraded") return "yellow";
  if (normalized === "offline") return "red";
  return "gray";
};

const levelColor = (level) => {
  const normalized = String(level || "").toLowerCase();
  if (normalized === "error") return "red";
  if (normalized === "warn") return "yellow";
  if (normalized === "info") return "blue";
  return "gray";
};

export default function PrinterHealth() {
  const toast = useToast();
  const titleColor = useColorModeValue("navy.700", "white");
  const textColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");

  const [agents, setAgents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [testPrinting, setTestPrinting] = useState(false);

  const [selectedPrinterId, setSelectedPrinterId] = useState("kitchen-main");
  const [testMessage, setTestMessage] = useState("Test print from Admin Portal");
  const [logPrinterId, setLogPrinterId] = useState("");
  const [logLevel, setLogLevel] = useState("all");

  const loadAgents = async (silent = false) => {
    if (!silent) {
      setLoadingAgents(true);
    }
    try {
      const response = await getPrinterAgentStatuses();
      const data = response.data?.statuses || [];
      setAgents(data);

      if (!selectedPrinterId && data.length > 0) {
        setSelectedPrinterId(data[0].printerId || "kitchen-main");
      }
    } catch (error) {
      if (!silent) {
        toast({
          title: "Failed to load printer agents",
          description: error?.response?.data?.message || error.message,
          status: "error",
          duration: 3200,
          isClosable: true,
        });
      }
    } finally {
      if (!silent) {
        setLoadingAgents(false);
      }
    }
  };

  const loadLogs = async (silent = false) => {
    if (!silent) {
      setLoadingLogs(true);
    }
    try {
      const response = await getPrintAgentLogs({
        printerId: logPrinterId.trim() || undefined,
        level: logLevel !== "all" ? logLevel : undefined,
        limit: 200,
      });
      setLogs(response.data?.logs || []);
    } catch (error) {
      if (!silent) {
        toast({
          title: "Failed to load print logs",
          description: error?.response?.data?.message || error.message,
          status: "error",
          duration: 3200,
          isClosable: true,
        });
      }
    } finally {
      if (!silent) {
        setLoadingLogs(false);
      }
    }
  };

  useEffect(() => {
    loadAgents();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadAgents(true);
      loadLogs(true);
    }, AUTO_REFRESH_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logPrinterId, logLevel]);

  const printerIds = useMemo(() => {
    const set = new Set(agents.map((agent) => String(agent.printerId || "").trim()).filter(Boolean));
    if (!set.size) {
      set.add("kitchen-main");
    }
    return Array.from(set);
  }, [agents]);

  const summary = useMemo(() => {
    const totals = {
      total: agents.length,
      online: 0,
      degraded: 0,
      offline: 0,
      errorLogs: logs.filter((entry) => String(entry.level || "").toLowerCase() === "error").length,
    };

    agents.forEach((agent) => {
      const state = String(agent.status || "").toLowerCase();
      if (state === "online") totals.online += 1;
      else if (state === "degraded") totals.degraded += 1;
      else if (state === "offline") totals.offline += 1;
    });

    return totals;
  }, [agents, logs]);

  const handleTestPrint = async () => {
    setTestPrinting(true);
    try {
      await queueTestPrint({
        printerId: selectedPrinterId || "kitchen-main",
        message: testMessage || "Test print from Admin Portal",
      });

      toast({
        title: "Test print queued",
        description: `Queued for ${selectedPrinterId || "kitchen-main"}`,
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Test print failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3200,
        isClosable: true,
      });
    } finally {
      setTestPrinting(false);
    }
  };

  return (
    <Box pt="80px">
      <SimpleGrid columns={1} spacing="20px">
        <Card p="20px">
          <Flex justify="space-between" align="center" wrap="wrap" gap="10px" mb="12px">
            <Box>
              <Text fontSize="xl" fontWeight="700" color={titleColor}>
                Printer Health
              </Text>
              <Text fontSize="sm" color={textColor}>
                Monitor agent heartbeat, check logs, and run test prints. Auto-refresh every 15s.
              </Text>
            </Box>
            <HStack spacing="8px">
              <Button onClick={() => loadAgents(false)} isLoading={loadingAgents}>
                Refresh Agents
              </Button>
              <Button onClick={() => loadLogs(false)} isLoading={loadingLogs}>
                Refresh Logs
              </Button>
            </HStack>
          </Flex>

          <SimpleGrid columns={{ base: 2, md: 5 }} spacing="10px" mb="12px">
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="10px" p="10px">
              <Text fontSize="xs" color={textColor}>Total Agents</Text>
              <Text fontSize="xl" fontWeight="800" color={titleColor}>{summary.total}</Text>
            </Box>
            <Box borderWidth="1px" borderColor="green.200" borderRadius="10px" p="10px">
              <Text fontSize="xs" color={textColor}>Online</Text>
              <Text fontSize="xl" fontWeight="800" color="green.500">{summary.online}</Text>
            </Box>
            <Box borderWidth="1px" borderColor="yellow.200" borderRadius="10px" p="10px">
              <Text fontSize="xs" color={textColor}>Degraded</Text>
              <Text fontSize="xl" fontWeight="800" color="yellow.500">{summary.degraded}</Text>
            </Box>
            <Box borderWidth="1px" borderColor="red.200" borderRadius="10px" p="10px">
              <Text fontSize="xs" color={textColor}>Offline</Text>
              <Text fontSize="xl" fontWeight="800" color="red.500">{summary.offline}</Text>
            </Box>
            <Box borderWidth="1px" borderColor="red.200" borderRadius="10px" p="10px">
              <Text fontSize="xs" color={textColor}>Error Logs</Text>
              <Text fontSize="xl" fontWeight="800" color="red.500">{summary.errorLogs}</Text>
            </Box>
          </SimpleGrid>

          <HStack spacing="8px" wrap="wrap">
            <Select value={selectedPrinterId} onChange={(e) => setSelectedPrinterId(e.target.value)} w="220px">
              {printerIds.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </Select>
            <Input
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Test message"
              maxW="380px"
            />
            <Button colorScheme="blue" onClick={handleTestPrint} isLoading={testPrinting}>
              Send Test Print
            </Button>
          </HStack>
        </Card>

        <Card p="20px">
          <Text fontSize="lg" fontWeight="700" color={titleColor} mb="10px">
            Agents
          </Text>

          {!agents.length ? (
            <Text color={textColor}>No agent heartbeats yet.</Text>
          ) : (
            <Stack spacing="10px">
              {agents.map((agent) => (
                <Box key={agent._id} borderWidth="1px" borderColor="gray.200" borderRadius="12px" p="12px">
                  <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
                    <Text fontWeight="800" color={titleColor}>
                      {agent.printerId}
                    </Text>
                    <HStack spacing="8px">
                      <Badge colorScheme={statusColor(agent.status)}>{agent.status || "unknown"}</Badge>
                      <Badge colorScheme="purple">{agent.connectionType || "-"}</Badge>
                    </HStack>
                  </Flex>

                  <Text fontSize="sm" color={textColor}>
                    Agent: {agent.agentId || "-"} • Last heartbeat: {formatDate(agent.lastHeartbeatAt)}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Target: {agent.printerHost || "-"}
                    {agent.printerPort ? `:${agent.printerPort}` : ""}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Version: {agent.appVersion || "-"}
                  </Text>
                  {agent.lastError ? (
                    <Text fontSize="sm" color="red.500">
                      Last error: {agent.lastError}
                    </Text>
                  ) : null}
                </Box>
              ))}
            </Stack>
          )}
        </Card>

        <Card p="20px">
          <Flex justify="space-between" align="center" wrap="wrap" gap="8px" mb="10px">
            <Text fontSize="lg" fontWeight="700" color={titleColor}>
              Agent Logs
            </Text>

            <HStack spacing="8px" wrap="wrap">
              <Input
                placeholder="Printer ID (optional)"
                value={logPrinterId}
                onChange={(e) => setLogPrinterId(e.target.value)}
                maxW="220px"
              />
              <Select value={logLevel} onChange={(e) => setLogLevel(e.target.value)} w="120px">
                {LOG_LEVELS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
              <Button onClick={() => loadLogs(false)} isLoading={loadingLogs}>
                Apply
              </Button>
            </HStack>
          </Flex>

          {!logs.length ? (
            <Text color={textColor}>No logs found.</Text>
          ) : (
            <Stack spacing="10px">
              {logs.map((log) => (
                <Box key={log._id} borderWidth="1px" borderColor="gray.200" borderRadius="12px" p="12px">
                  <Flex justify="space-between" align="center" wrap="wrap" gap="8px">
                    <Text fontWeight="700" color={titleColor}>
                      {log.printerId}
                    </Text>
                    <HStack spacing="8px">
                      <Badge colorScheme={levelColor(log.level)}>{log.level || "info"}</Badge>
                      <Badge colorScheme="gray">{log.agentId || "-"}</Badge>
                    </HStack>
                  </Flex>

                  <Text fontSize="sm" color={textColor}>
                    {formatDate(log.createdAt)}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    {log.message}
                  </Text>
                  {log.meta && Object.keys(log.meta).length ? (
                    <Box mt="6px" p="8px" bg="gray.50" borderRadius="8px">
                      <Text fontSize="xs" color={textColor}>
                        {JSON.stringify(log.meta)}
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Stack>
          )}
        </Card>
      </SimpleGrid>
    </Box>
  );
}
